import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';

/**
 * Lightweight contextual product tour.
 * Usage:
 *   const steps = [
 *     { selector: '[data-tour="x"]', title: '...', content: '...' },
 *     ...
 *   ];
 *   <FeatureTour tourId="branch-summary-v1" steps={steps} />
 *
 * - Auto-runs once on first visit (tracked in localStorage under tour_seen_<id>)
 * - Can be replayed via <FeatureTour.ReplayButton tourId="..." /> button
 */

const PADDING = 8;

function computePosition(rect, popoverW, popoverH, placement = 'auto') {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const below = rect.bottom + 12;
  const above = rect.top - popoverH - 12;
  const right = rect.right + 12;
  const left = rect.left - popoverW - 12;

  const spaces = {
    bottom: vh - rect.bottom,
    top: rect.top,
    right: vw - rect.right,
    left: rect.left,
  };
  let final = placement;
  if (placement === 'auto') {
    const best = Object.entries(spaces).sort((a, b) => b[1] - a[1])[0][0];
    final = best;
  }

  if (final === 'bottom' || (final === 'auto' && spaces.bottom > popoverH + 24)) {
    return { top: below, left: Math.min(Math.max(rect.left + rect.width / 2 - popoverW / 2, 8), vw - popoverW - 8) };
  }
  if (final === 'top') {
    return { top: Math.max(above, 8), left: Math.min(Math.max(rect.left + rect.width / 2 - popoverW / 2, 8), vw - popoverW - 8) };
  }
  if (final === 'right') return { top: Math.min(Math.max(rect.top, 8), vh - popoverH - 8), left: Math.min(right, vw - popoverW - 8) };
  if (final === 'left') return { top: Math.min(Math.max(rect.top, 8), vh - popoverH - 8), left: Math.max(left, 8) };
  return { top: below, left: rect.left };
}

export function FeatureTour({ tourId, steps, forceStart = false, onClose, startDelay = 1500 }) {
  const [active, setActive] = useState(false);
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const popRef = useRef(null);

  const key = `tour_seen_${tourId}`;

  // Auto-start — wait a bit longer so async data / animations can settle
  useEffect(() => {
    if (forceStart) { setActive(true); setIdx(0); return; }
    try {
      if (!localStorage.getItem(key)) {
        const t = setTimeout(() => setActive(true), startDelay);
        return () => clearTimeout(t);
      }
    } catch {}
  }, [forceStart, key, startDelay]);

  // iter466 — Escape key closes the tour. Previously the only way to dismiss
  // was clicking the X button or the backdrop, which caused users to think
  // the tour was stuck when the popover scrolled off-screen.
  useEffect(() => {
    if (!active) return;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const updatePosition = useCallback(() => {
    if (!active || !steps[idx]) return;
    const step = steps[idx];

    // Try to find element — retry a few times if DOM not ready yet (async data loads)
    const findAndPosition = (retries = 0) => {
      const el = document.querySelector(step.selector);
      if (!el) {
        if (retries < 10) {
          // Element not yet mounted — retry every 200ms up to 2s
          setTimeout(() => findAndPosition(retries + 1), 200);
          return;
        }
        // iter466 — Give up: auto-close the tour instead of leaving a
        // blank-but-click-blocking backdrop on the page (this caused the
        // "Cost of Goods click does nothing" bug on /branch-summary).
        try { localStorage.setItem(key, '1'); } catch {}
        setActive(false);
        onClose?.();
        return;
      }
      el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
      setTimeout(() => {
        const r = el.getBoundingClientRect();
        // Safety: if the element is hidden (zero size), auto-close.
        if (r.width === 0 && r.height === 0) {
          try { localStorage.setItem(key, '1'); } catch {}
          setActive(false);
          onClose?.();
          return;
        }
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height, bottom: r.bottom, right: r.right });
        const pW = 340, pH = popRef.current?.offsetHeight || 180;
        setPos(computePosition(r, pW, pH, step.placement));
      }, 350);
    };
    findAndPosition();
  }, [active, idx, steps, key, onClose]);

  useEffect(() => {
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [updatePosition]);

  const close = () => {
    setActive(false);
    try { localStorage.setItem(key, '1'); } catch {}
    onClose?.();
  };
  const next = () => { if (idx < steps.length - 1) setIdx(i => i + 1); else close(); };
  const prev = () => { if (idx > 0) setIdx(i => i - 1); };

  if (!active || !steps.length) return null;
  const step = steps[idx];

  return (
    <AnimatePresence>
      <motion.div
        key="tour-backdrop"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[9998] pointer-events-none"
        style={{
          background: rect
            ? `radial-gradient(ellipse ${rect.width / 2 + PADDING + 40}px ${rect.height / 2 + PADDING + 40}px at ${rect.left + rect.width / 2}px ${rect.top + rect.height / 2}px, transparent 0%, transparent 60%, rgba(15,23,42,0.7) 100%)`
            : 'rgba(15,23,42,0.6)',
        }}
      />
      {rect && (
        <motion.div
          key="tour-highlight"
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.25 }}
          className="fixed z-[9999] pointer-events-none border-2 border-white rounded-lg shadow-[0_0_0_4px_rgba(59,130,246,0.5)]"
          style={{
            top: rect.top - PADDING,
            left: rect.left - PADDING,
            width: rect.width + PADDING * 2,
            height: rect.height + PADDING * 2,
          }}
        />
      )}
      <motion.div
        key="tour-popover"
        ref={popRef}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        onClick={(e) => e.stopPropagation()}
        className="fixed z-[9999] w-[340px] bg-white rounded-xl shadow-2xl border border-border overflow-hidden"
        style={rect ? { top: pos.top, left: pos.left } : { top: '50%', left: '50%', transform: 'translate(-50%,-50%)' }}
        data-testid="feature-tour-popover"
      >
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-2 text-white">
            <Sparkles size={14} />
            <span className="text-[11px] font-semibold uppercase tracking-wider">What's new</span>
          </div>
          <button onClick={close} className="text-white/80 hover:text-white" data-testid="tour-close"><X size={16} /></button>
        </div>
        <div className="p-4">
          <h3 className="text-sm font-bold text-foreground mb-1.5">{step.title}</h3>
          <p className="text-xs text-muted-foreground leading-relaxed">{step.content}</p>
        </div>
        <div className="px-4 py-3 bg-muted/50 border-t border-border flex items-center justify-between">
          <span className="text-[10px] text-muted-foreground font-medium">{idx + 1} / {steps.length}</span>
          <div className="flex items-center gap-2">
            {idx > 0 && (
              <button onClick={prev} className="flex items-center gap-1 px-2.5 py-1 text-xs text-muted-foreground hover:bg-muted rounded-md" data-testid="tour-prev">
                <ChevronLeft size={12} /> Back
              </button>
            )}
            <button onClick={next} className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-info hover:bg-blue-700 rounded-md" data-testid="tour-next">
              {idx === steps.length - 1 ? 'Got it' : 'Next'} <ChevronRight size={12} />
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

/**
 * Button to replay a specific tour. Resets localStorage flag and force-starts it.
 * Usage: <ReplayTourButton tourId="..." onStart={() => setForce(true)} />
 */
export function useTourReplay(tourId) {
  const [force, setForce] = useState(0);
  const replay = () => {
    try { localStorage.removeItem(`tour_seen_${tourId}`); } catch {}
    setForce(f => f + 1);
  };
  return { replayKey: force, replay, forceStart: force > 0 };
}
